import React, { useCallback, useMemo } from 'react';
import { CompositeDataSetEmployeeCandidateMatchingRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { DataSetCompositeReportsConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeReportsConfigComponent';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { DataSetCompositeReportsFinalReviewComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeReportsFinalReviewComponent';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { UseGenerateReportsInsightsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-generate-reports-insights';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const EmployeeCandidateMatchingConfigStep = z.enum([
  'reports',
  'column_matching',
  'final-review',
  'complete',
]);

type EmployeeCandidateMatchingConfigStep = z.infer<
  typeof EmployeeCandidateMatchingConfigStep
>;

export const DataSetCompositeRecipeEmployeeCandidateMatchingConfigComponent = ({
  config,
  setConfig,
  insightsPayload,
}: {
  config: CompositeDataSetEmployeeCandidateMatchingRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
  insightsPayload: UseGenerateReportsInsightsPayload;
}) => {
  const language = useLanguage();
  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<EmployeeCandidateMatchingConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'reports', display: 'Reports' },
        { value: 'column_matching', display: 'Column Matching' },
        { value: 'final-review', display: 'Final Review' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const setColumnMatching = useCallback(
    ({
      columnToMatch,
      rowIndex,
    }: {
      rowIndex: number;
      columnToMatch: CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch;
    }) => {
      if (rowIndex === 0) {
        setConfig({
          ...config,
          idColumn: columnToMatch,
        });
      } else if (rowIndex === 1) {
        setConfig({
          ...config,
          firstNameColumnToMatch: columnToMatch,
        });
      } else if (rowIndex === 2) {
        setConfig({
          ...config,
          lastNameColumnToMatch: columnToMatch,
        });
      }
    },
    [config, setConfig],
  );

  const columnsToMatch = useMemo(() => {
    return [
      {
        id: 'ID',
        ...config.idColumn,
      },
      {
        id: 'First Name',
        ...config.firstNameColumnToMatch,
      },
      {
        id: 'Last Name',
        ...config.lastNameColumnToMatch,
      },
    ];
  }, [config]);

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<EmployeeCandidateMatchingConfigStep>(
      {
        items: stepItems,
        onNext: () => true,
      },
    );

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'reports':
        return (
          <DataSetCompositeReportsConfigComponent
            reports={config.reports}
            setReports={(reports) => {
              setConfig({ ...config, reports });
            }}
            insightsPayload={insightsPayload}
          />
        );
      case 'column_matching':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <Table
              columnDefinitions={[
                {
                  id: 'id',
                  header: () => <div>ID</div>,
                  cell: ({ item }) => <div>{item.id}</div>,
                },
                {
                  id: 'employeeColumn',
                  header: () => <div>Employee Column</div>,
                  cell: ({ item, rowIndex }) => (
                    <input
                      className="form-input"
                      value={item.employeeColumn}
                      onChange={(e) => {
                        setColumnMatching({
                          rowIndex,
                          columnToMatch: {
                            ...item,
                            employeeColumn: e.target.value,
                          },
                        });
                      }}
                    />
                  ),
                },
                {
                  id: 'candidateColumn',
                  header: () => <div>Candidate Column</div>,
                  cell: ({ item, rowIndex }) => (
                    <input
                      className="form-input"
                      value={item.candidateColumn}
                      onChange={(e) => {
                        setColumnMatching({
                          rowIndex,
                          columnToMatch: {
                            ...item,
                            candidateColumn: e.target.value,
                          },
                        });
                      }}
                    />
                  ),
                },
              ]}
              items={columnsToMatch}
            />
          </Spacing>
        );
      case 'final-review':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <DataSetCompositeReportsFinalReviewComponent
              reports={config.reports}
            />

            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={language.dangerousUserInput({
                value: 'Column Matchings',
              })}
            />

            {columnsToMatch.map((matching, index) => {
              return (
                <Spacing key={index} size={'md'} direction={'horizontal'}>
                  <Box pl={'md'}>
                    <S2Typography
                      variant={'headline-6-alt'}
                      translatedContent={language.dangerousUserInput({
                        value: `${matching.employeeColumn} <-> ${matching.candidateColumn}`,
                      })}
                    />
                  </Box>
                </Spacing>
              );
            })}
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [
    dataWizardPayload.selectedItem,
    config,
    insightsPayload,
    columnsToMatch,
    language,
    setConfig,
    setColumnMatching,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
